/**
 * What a report is called before anybody has called it anything.
 *
 * THREE FUNCTIONS, and they are deliberately in one file because they are one
 * idea seen from three sides: {@link describeSpec} says what a draft IS,
 * {@link suggestReportName} turns that sentence into a name, and
 * {@link freeReportName} makes that name unique in the list it is about to join.
 *
 * Here rather than in the host app because the sentence is already the
 * builder's — it is the heading an unnamed report carries — and a host that
 * re-derived it would be free to suggest a name that reads nothing like the
 * heading the reader is looking at while they decide whether to accept it.
 */
import {
  findDimension,
  findMeasure,
  type ReportModel,
  type ReportSpec,
} from '@clockwise/semantic-core';

/** Longest report name that will be stored. Mirrors the server's validator. */
export const MAX_REPORT_NAME = 120;

/**
 * Longest description that will be stored. Mirrors the server's validator.
 *
 * Longer than a name by a lot, because it answers a different question: a name
 * is how you find the report again, a description is what a recipient reads to
 * decide whether to run it.
 */
export const MAX_REPORT_DESCRIPTION = 500;

/**
 * The draft in words — "Utilized Hours by Employee across Month".
 *
 * The heading of an unnamed report, the sub-line of a named one, and the basis
 * of every suggestion below.
 */
export function describeSpec(model: ReportModel, spec: ReportSpec): string {
  if (spec.kind === 'detail') {
    const n = spec.columns.length;
    return `Every row, with ${n} column${n === 1 ? '' : 's'}`;
  }
  const measures = spec.measures.map((id) => findMeasure(model, id)?.label ?? id).join(' + ');
  const rows = spec.rows.map((id) => findDimension(model, id)?.label ?? id);
  const cols = spec.cols.map((id) => findDimension(model, id)?.label ?? id);
  return `${measures} by ${rows.join(' × ')}${cols.length ? ` across ${cols.join(' × ')}` : ''}`;
}

/**
 * What to call this draft, if its author does not.
 *
 * A PIVOT is named after what it measures — "Utilized Hours by Employee" is
 * both an accurate name and the sentence already on screen, so accepting the
 * suggestion costs a glance rather than a decision.
 *
 * A DETAIL listing is not: {@link describeSpec} calls it "Every row, with 24
 * columns", which describes every detail report ever built and identifies none
 * of them. So a listing takes its subject area instead — "Timesheets detail" —
 * which at least says what it is a listing OF. The column count is still there
 * under the name, where it belongs, as the sub-line.
 *
 * Never returns the empty string: an unrunnable draft cannot be saved, but a
 * suggestion is asked for while one is being edited, and "" would render as a
 * silently blank field the reader has to notice for themselves.
 */
export function suggestReportName(model: ReportModel, spec: ReportSpec): string {
  if (spec.kind === 'detail') return truncate(`${model.label} detail`);
  // A pivot missing either half has no sentence yet — `describeSpec` would
  // return " by " and the suggestion would read as a bug rather than a default.
  if (!spec.measures.length || !spec.rows.length) return truncate(`${model.label} report`);
  return truncate(describeSpec(model, spec).trim() || `${model.label} report`);
}

/**
 * `base`, or the first free `base (2)`, `base (3)`, … not already in `taken`.
 *
 * NO `(1)`. A lone "(1)" announces a series that does not exist, and the first
 * report of its kind is not the first of anything — the suffix appears only
 * once there is genuinely something to tell apart.
 *
 * Case-insensitive, because the server's unique index is: "My hours" and "my
 * hours" collide there, and a suggestion that ignored that would be refused on
 * submit for a reason the reader cannot see.
 *
 * This is a SUGGESTION and not a guarantee. The list it reads may be a moment
 * stale and two tabs can pick the same number, so the server still owns
 * uniqueness and still answers 409 — this exists so that the common case never
 * has to.
 */
export function freeReportName(base: string, taken: Iterable<string>): string {
  const used = new Set([...taken].map((n) => n.trim().toLowerCase()));
  const trimmed = truncate(base.trim());
  if (!used.has(trimmed.toLowerCase())) return trimmed;
  // Bounded: after a hundred of them the person wants a name, not a hundred and
  // first suffix, and the last candidate is returned for the server to refuse
  // rather than silently saving over the intent.
  for (let n = 2; n <= 100; n++) {
    const suffix = ` (${n})`;
    // Trim the BASE, not the suffix — a name cut to fit that then loses the
    // number it was cut for would collide with the one before it.
    const candidate = truncate(trimmed, MAX_REPORT_NAME - suffix.length) + suffix;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
  return trimmed;
}

function truncate(value: string, max = MAX_REPORT_NAME): string {
  return value.length <= max ? value : value.slice(0, max).trimEnd();
}
